import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';

import { Order, OrderHistory } from '@spartacus/order/root';
import { ImageType, Product } from '@spartacus/core';

import { IOrderDetails, IOrderInformation, IOrderProduct, IOrderProductDetail, IOrderStatus } from '@seb/web-design-system/account';
import { ILink, IProductCustomer, IWarranty, StatusEnum } from '@seb/web-design-system/core';
import { IAddressCard, ICheckoutTotal } from '@seb/web-design-system/checkout';

import { CartGroupEntry, CustomConsignment, CustomOrder, CustomOrderEntry, CustomPromotionResult } from '../models';
import { CartGroupTypeEnum, DeliveryModeEnum, OrderStatusEnum, StockStatusEnum } from '../enums';
import { ImageService } from '../services/image.service';
import { StockService } from '../services/stock.service';
import { getMediaByFormat } from '../utils/media.utils';
import { DocumentMapper } from './document.mapper';
import { toAddressCard } from './address.mapper';
import { LinkMapper } from './link.mapper';
import { HobMapper } from './hob.mapper';
import { containsExpressCheckout } from '../utils/address.utils';

export interface IOrderProductWithGroupNumber extends IOrderProduct {
  groupNumber?: number;
}

export interface OrderParams {
  order: CustomOrder;
  isTaxesIncluded?: boolean;
  isReturnEligible?: boolean;
  isNewPriceManagementActivated?: boolean;
  hasInvoiceDownload?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OrderMapper {
  constructor(
    private documentMapper: DocumentMapper,
    private imageService: ImageService,
    private translateService: TranslateService,
    private readonly hobMapper: HobMapper,
    private readonly stockService: StockService,
    private readonly linkMapper: LinkMapper
  ) {}

  orderHistoryToIOrderDetails(orderHistory: OrderHistory): IOrderDetails {
    return {
      code: orderHistory.code,
      date: new Date(orderHistory.placed)?.toLocaleDateString(),
      totalPrice: orderHistory.total?.formattedValue,
      total: orderHistory.total ? {
        currencyIso: orderHistory.total.currencyIso,
        formattedValue: orderHistory.total.formattedValue,
        priceType: orderHistory.total.priceType,
        value: orderHistory.total.value
      } : undefined,
      status: this.getOrderStatus(orderHistory)
    };
  }

  orderToIOrderDetails(params: OrderParams): IOrderDetails {
    const { order, isTaxesIncluded, isReturnEligible, isNewPriceManagementActivated, hasInvoiceDownload } = params;
    const isExpressCheckout = containsExpressCheckout(order?.deliveryAddress?.addressName);
    const shippingAddress = isExpressCheckout ? order.deliveryAddress : order.depositWithdrawalAddress || order.deliveryAddress;
    const showPaymentMode: boolean = order?.totalPrice?.value > 0;
    const orderDetails = {
      code: order.code,
      date: new Date(order.created)?.toLocaleDateString(),
      totalPrice: order.totalPrice?.formattedValue,
      total: order.totalPrice ? {
        currencyIso: order.totalPrice.currencyIso,
        formattedValue: order.totalPrice.formattedValue,
        priceType: order.totalPrice.priceType,
        value: order.totalPrice.value
      } : undefined,
      status: this.getOrderStatus(order),
      shippingAddress: toAddressCard(shippingAddress),
      billingAddress: toAddressCard(order.paymentAddress),
      paymentMode: showPaymentMode
        ? {
          mode: order.paymentModeDetail?.name,
          medias: [this.imageService.normalizeMedia(order.paymentModeDetail?.media)],
          paymentProvider: order?.paymentModeDetail?.paymentProvider ?? '',
          selectedPaymentMode: order?.selectedPaymentMode ?? ''
        }
        : undefined,
      shipments: order.consignments.map((consignment) =>
        this.consignmentToIOrderProductDetail(order, OrderStatusEnum[order.status], consignment, isNewPriceManagementActivated)
      ),
      isTaxesIncluded: isTaxesIncluded,
      isReturnEligible: isReturnEligible ?? false,
      hasInvoiceDownload: hasInvoiceDownload ?? false
    };
    const cancelledProducts: IOrderProduct[] = [];
    if (order.unconsignedEntries?.length > 0) {
      orderDetails.shipments.push(this.unconsignedEntriesToIOrderProductDetail(order, isNewPriceManagementActivated));
    }

    order.entryGroups?.forEach((entryGroup) =>
      entryGroup.entries?.forEach((entry: CustomOrderEntry) => {
        if (entry.cancelledQuantity > 0) {
          cancelledProducts.push(this.productToIOrderProduct(entry.product, entry.cancelledQuantity));
        }
      })
    );

    if (cancelledProducts.length) {
      orderDetails.shipments.push({
        products: cancelledProducts,
        status: this.getConsignmentStatus({ status: OrderStatusEnum.CANCELLED })
      });
    }

    orderDetails.shipments.forEach((entry) => {
      entry.products.forEach((product) => {
        product.productInfo.isTaxesIncluded = isTaxesIncluded;
        product.productInfo.stock = undefined;
      });
    });

    return orderDetails;
  }

  orderToIOrderInformation(order: CustomOrder): IOrderInformation {
    const orderDetails = {
      code: order.code,
      date: new Date(order.created)?.toLocaleDateString(),
      totalPrice: order.totalPrice?.formattedValue,
      total: order.totalPrice ? {
        currencyIso: order.totalPrice.currencyIso,
        formattedValue: order.totalPrice.formattedValue,
        priceType: order.totalPrice.priceType,
        value: order.totalPrice.value
      } : undefined,
      status: this.getOrderStatus(order),
      products: [],
      link: { url: `/my-orders?orderCode=${order.code}`, linkName: 'order.seeDetails', external: false }
    };
    if (order.consignments?.length > 0) {
      order.consignments.forEach((consignment) => {
        consignment.entries.forEach((entry) => {
          const product = entry.orderEntry.product;
          orderDetails.products.push(getMediaByFormat(product?.primaryImages, 'thumbnail') ?? product?.images?.[ImageType.PRIMARY]?.['thumbnail']);
        });
      });
    }
    if (order.unconsignedEntries?.length > 0) {
      order.unconsignedEntries.forEach((consignment) => {
        const product = consignment?.product;
        orderDetails.products.push(getMediaByFormat(product?.primaryImages, 'thumbnail') ?? product?.images?.[ImageType.PRIMARY]?.['thumbnail']);
      });
    }

    return orderDetails;
  }

  cartGroupEntryToIProductCustomer(group: CartGroupEntry, isReadOnly?: boolean, isHob?: boolean): IProductCustomer {
    const hasUrl = group.entries?.length > 0 && !!group.entries[0]?.product?.url && !!group.externalReferenceId;
    return {
      code: !isReadOnly ? group.externalReferenceId : '',
      title: group.label,
      media: group.medias?.[ImageType.GALLERY]?.['main'],
      price: group.discountedPrice?.formattedValue || group.basePrice?.formattedValue,
      discountedPrice:
        group.totalPriceWithoutDiscounts?.value !== group.totalPrice?.value
          ? group.totalPrice?.formattedValue
          : undefined,
      totalPrice: group.totalPriceWithoutDiscounts?.formattedValue,
      quantity: group.selectedQuantity,
      url: hasUrl ? `${group.entries[0]?.product?.url}?bundle=${group.externalReferenceId}` : undefined,
      maxQty: group.maxQuantitySelectable,
      brandLogoTitle: isHob ? this.hobMapper.getBrandLogoTitle(group.entries[0]?.product?.brand) : null
    };
  }

  orderEntryToIOrderProduct(
    orderEntry: CustomOrderEntry,
    quantity?: number,
    isReadOnly?: boolean,
    shippedTotalPrice?: string,
    shippedDiscountedPrice?: string,
    warrantyOffers?: IWarranty[],
    isExtendedWarrantyEnabled?: boolean,
    isNewPriceManagementActivated?: boolean,
    isHob?: boolean
  ): IOrderProduct {
    const product = orderEntry.product;
    const links: ILink[] = [];
    if (!!product && product.instructionsForUse?.instructionsForUse?.length > 0) {
      links.push({
        url: `/instructions-for-use/csp/${product.code}`,
        linkName: 'order.details.links.ifus',
        external: false
      });
    }
    if (!!product && product.faqs?.faqEntries?.length > 0) {
      links.push({ url: `/faq/csp/${product.code}`, linkName: 'order.details.links.faqs', external: false });
    }
    const media = getMediaByFormat(product?.primaryImages, 'main') ?? product?.images?.[ImageType.PRIMARY]?.['main'];

    return {
      productInfo: {
        code: !isReadOnly ? product?.commercialCode : '',
        media,
        title: product?.name,
        price: orderEntry.product?.discountedPrice?.formattedValue || orderEntry.basePrice?.formattedValue,
        discountedPrice: shippedDiscountedPrice
          ? shippedDiscountedPrice
          : orderEntry.totalPriceWithoutDiscounts?.value !== orderEntry.totalPrice?.value
            ? orderEntry.totalPrice?.formattedValue
            : undefined,
        recommendedPrice: isNewPriceManagementActivated ? orderEntry.product?.recommendedPrice?.formattedValue : undefined,
        isDiscountWithRecommendedPrice: !isNewPriceManagementActivated ? !product?.strikethroughPrice : false,
        isAnAlreadyDiscountedProduct: isNewPriceManagementActivated ? !!product?.formattedDiscountValue : false,
        totalPrice: shippedTotalPrice ?? orderEntry.totalPriceWithoutDiscounts?.formattedValue,
        quantity: quantity || orderEntry?.quantity,
        url: this.linkMapper.getBrandUrl(product?.brand, LinkMapper.decodeUrl(product?.url)),
        maxQty: product?.maxOrderQuantity,
        indexFollow: product?.indexFollow,
        extendedWarrantyOffer: orderEntry.extendedWarrantyOffer?.name && isExtendedWarrantyEnabled ? {
          title: orderEntry.extendedWarrantyOffer?.name,
          price: orderEntry.extendedWarrantyOffer?.price?.formattedValue,
          footnote: this.translateService.instant('addToCart.extendedWarranty.legal.notice')
        } : null,
        isFree: product?.productGenre === 'FREE_GIFT' && product?.price?.value === 0,
        warrantyOffers,
        brandLogoTitle: isHob && !product?.isAccessory ? this.hobMapper.getBrandLogoTitle(product?.brand) : null,
        stock: {
          online: product?.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
            product?.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK,
          lowStock: this.stockService.checkStock(product?.price?.value, product?.stock?.stockLevel)
        },
        basePrice: {
          formattedValue: orderEntry.basePrice?.formattedValue,
          currencyIso: orderEntry.basePrice?.currencyIso,
          priceType: orderEntry.basePrice?.priceType,
          value: orderEntry.basePrice?.value
        }
      },
      links
    };
  }

  getOrderDetailsTotal(orderDetails: CustomOrder): ICheckoutTotal {
    let totalEcoTaxValue = orderDetails.totalEcoTax?.formattedValue;
    totalEcoTaxValue = totalEcoTaxValue?.replaceAll(' ', '').replace(',', '.');
    totalEcoTaxValue = totalEcoTaxValue?.substring(0, totalEcoTaxValue?.length - 1);

    return {
      subtotal: orderDetails?.subTotalPriceWithProductDiscounts
        ? orderDetails?.subTotalPriceWithProductDiscounts?.formattedValue
        : '',
      shippingCosts: orderDetails?.deliveryCost ? orderDetails?.deliveryCost?.formattedValue : '',
      paymentCosts: orderDetails?.paymentCost ? orderDetails?.paymentCost?.formattedValue : '',
      savings:
        !!orderDetails?.totalDiscountsWithoutDeliveryCost && orderDetails.totalDiscountsWithoutDeliveryCost.value > 0
          ? orderDetails?.totalDiscountsWithoutDeliveryCost?.formattedValue
          : '',
      ecoPart: parseFloat(totalEcoTaxValue) > 0 ? totalEcoTaxValue : null,
      total: orderDetails?.totalPrice ? orderDetails?.totalPrice?.formattedValue : '',
      paymentMode: orderDetails?.paymentModeDetail ? orderDetails?.paymentModeDetail?.name : '',
      selectedPaymentMode: orderDetails?.selectedPaymentMode ?? '',
      paymentProvider: orderDetails?.paymentModeDetail?.paymentProvider ?? ''
    };
  }

  getOrderDetailsAddress(orderDetails: CustomOrder, isExpressCheckout?: boolean): IAddressCard[] {
    const clientAddresses: IAddressCard[] = [];
    const isRepair = orderDetails.isForfait && !orderDetails.serviceShippingIncluded;
    const isWithdrawal = orderDetails.isForfait && orderDetails.serviceShippingIncluded;
    const deliveryModeType = orderDetails?.deliveryMode?.type;
    const addressName = orderDetails?.depositWithdrawalAddress?.addressName
      && (deliveryModeType === DeliveryModeEnum.RELAYPOINT
        || (!isExpressCheckout && !containsExpressCheckout(orderDetails.depositWithdrawalAddress.addressName)))
      ? orderDetails.depositWithdrawalAddress.addressName
      : '';

    const delivery: IAddressCard = {
      postalCode: !!orderDetails?.depositWithdrawalAddress?.postalCode && deliveryModeType === DeliveryModeEnum.RELAYPOINT
        ? orderDetails?.depositWithdrawalAddress?.postalCode
        : orderDetails?.deliveryAddress?.postalCode
          ? orderDetails?.deliveryAddress?.postalCode
          : '',
      firstname: !!orderDetails?.depositWithdrawalAddress?.firstName && deliveryModeType === DeliveryModeEnum.RELAYPOINT
        ? ''
        : !!orderDetails?.deliveryAddress?.firstName && deliveryModeType !== DeliveryModeEnum.RELAYPOINT
          ? orderDetails?.deliveryAddress?.firstName
          : '',
      lastname: !!orderDetails?.depositWithdrawalAddress?.lastName && deliveryModeType === DeliveryModeEnum.RELAYPOINT
        ? ''
        : !!orderDetails?.deliveryAddress?.lastName && deliveryModeType !== DeliveryModeEnum.RELAYPOINT
          ? orderDetails?.deliveryAddress?.lastName
          : '',
      addressName: addressName,
      city: !!orderDetails?.depositWithdrawalAddress?.town && deliveryModeType === DeliveryModeEnum.RELAYPOINT
        ? orderDetails?.depositWithdrawalAddress?.town
        : orderDetails?.deliveryAddress?.town
          ? orderDetails?.deliveryAddress?.town
          : '',
      address: !!orderDetails?.depositWithdrawalAddress?.streetName && deliveryModeType === DeliveryModeEnum.RELAYPOINT
        ? orderDetails?.depositWithdrawalAddress?.streetName
        : orderDetails?.deliveryAddress?.streetName
          ? orderDetails?.deliveryAddress?.streetName
          : '',
      title: this.translateService.instant(
        isRepair
          ? 'checkout.address.repairTitle'
          : isWithdrawal
            ? 'checkout.address.withdrawalTitle'
            : isExpressCheckout
              ? 'expressCheckout.recap.deliveryMode'
              : 'checkout.address.shippingTitle'),
      details: isExpressCheckout
        ? {
          title: orderDetails?.deliveryMode?.name,
          info: orderDetails?.deliveryCost?.value === 0 ? this.translateService.instant('checkout.total.free') : orderDetails?.deliveryCost?.formattedValue
        }
        : undefined
    };
    clientAddresses.push(delivery);

    const addressNameBilling = orderDetails?.paymentAddress?.addressName && !isExpressCheckout
      && !containsExpressCheckout(orderDetails?.paymentAddress?.addressName)
      ? orderDetails.paymentAddress.addressName
      : '';

    // TODO-ABO: utiliser la bonne property si paypal, googlePay ou applePay
    const billing: IAddressCard = {
      postalCode: orderDetails?.paymentAddress?.postalCode ? orderDetails?.paymentAddress?.postalCode : '',
      firstname: orderDetails?.paymentAddress?.firstName ? orderDetails?.paymentAddress?.firstName : '',
      lastname: orderDetails?.paymentAddress?.lastName ? orderDetails?.paymentAddress?.lastName : '',
      addressName: addressNameBilling,
      city: orderDetails?.paymentAddress?.town ? orderDetails?.paymentAddress?.town : '',
      address: orderDetails?.paymentAddress?.streetName ? orderDetails?.paymentAddress?.streetName : '',
      title: this.translateService.instant(isExpressCheckout ? 'expressCheckout.recap.billingDetails' : 'checkout.address.billingTitle'),
      details: isExpressCheckout
        ? {
          title: this.translateService.instant('expressCheckout.recap.paypal.name'),
          info: orderDetails?.paymentAddress?.email,
          caption: this.translateService.instant('expressCheckout.recap.paypal.linkedEmailLabel')
        }
        : undefined
    };

    clientAddresses.push(billing);

    return clientAddresses;
  }

  getOrderDetailsOverview(
    orderDetails: CustomOrder,
    isTaxesIncluded?: boolean,
    isExtendedWarrantyEnabled?: boolean,
    isNewPriceManagementActivated?: boolean,
    isHob?: boolean,
    step?: number
  ): IProductCustomer[] {
    const productsOverview: IProductCustomer[] = [];
    orderDetails?.entryGroups?.forEach((orderGroup) => {
      if (orderGroup?.type === CartGroupTypeEnum.STANDALONE) {
        const product = this.orderEntryToIOrderProduct(
          orderGroup.entries[0],
          undefined,
          true,
          undefined,
          undefined,
          undefined,
          isExtendedWarrantyEnabled,
          isNewPriceManagementActivated,
          isHob
        )?.productInfo;

        const productPromo = orderDetails?.appliedProductPromotions?.find((promo) =>
          promo?.consumedEntries?.some((entry) => orderGroup?.entries[0]?.entryNumber === entry?.orderEntryNumber)
        ) as CustomPromotionResult;
        product['promo'] = productPromo?.promotion?.description
          ? productPromo?.promotion?.description
          : productPromo?.promotionActions?.at(0)?.usedCouponCodes?.at(0);

        if (step >= 2 && product?.stock?.lowStock) {
          product.stock.lowStock = undefined;
        }

        product.isTaxesIncluded = isTaxesIncluded;
        productsOverview.push(product);
      } else if (orderGroup?.type === CartGroupTypeEnum.BUNDLE) {
        if (orderGroup?.entries?.length > 0) {
          const product = this.cartGroupEntryToIProductCustomer(orderGroup, true);
          product.isTaxesIncluded = isTaxesIncluded;
          product['bundle'] = orderGroup?.entries?.map((entryBundle) => {
            const bundleProductInfo = this.orderEntryToIOrderProduct(
              entryBundle,
              undefined,
              true,
              undefined,
              undefined,
              undefined,
              undefined,
              isNewPriceManagementActivated,
              isHob
            )?.productInfo;
            bundleProductInfo.isTaxesIncluded = isTaxesIncluded;
            return bundleProductInfo;
          });
          productsOverview.push(product);
        }
      }
    });
    return productsOverview;
  }

  private consignmentToIOrderProductDetail(
    order: CustomOrder,
    orderStatus: OrderStatusEnum,
    consignment: CustomConsignment,
    isNewPriceManagementActivated?: boolean
  ): IOrderProductDetail {
    const links: ILink[] = [];
    let displayBundleWarningMessage = false;
    const consignmentEntryGroups = new Map([
      [CartGroupTypeEnum.STANDALONE, false],
      [CartGroupTypeEnum.BUNDLE, false]
    ]);
    if (
      [
        OrderStatusEnum.SHIPPED,
        OrderStatusEnum.PARTIALLY_DISPATCH,
        OrderStatusEnum.PARTIALLY_SHIPPED_CANCELLED
      ].includes(orderStatus) &&
      !!consignment.bill?.url
    ) {
      const url = this.documentMapper.normalizeUrl(consignment.bill.url);
      links.push({ url, linkName: 'order.details.links.downloadInvoice', external: true });
    }
    if (order.status === OrderStatusEnum.COMPLETED) {
      if (order.bill?.url) {
        const urlBill = this.documentMapper.normalizeUrl(order.bill.url);
        links.push({ url: urlBill, linkName: 'order.details.links.downloadInvoice', external: true });
      }

      if (order.voucherFileForService?.url) {
        const urlVoucher = this.documentMapper.normalizeUrl(order.voucherFileForService.url);
        links.push({ url: urlVoucher, linkName: 'order.details.links.downloadVoucherFileForService', external: true });
      }
    }
    if (!!consignment.trackingID && !!consignment.trackingURL) {
      links.push({ url: consignment.trackingURL, linkName: 'order.details.links.followOrder', external: true });
    }

    const products: IOrderProductWithGroupNumber[] = [];
    consignment.entries?.forEach((entry) => {
      const linkedEntryGroup = order.entryGroups?.find(
        (entryGroup) =>
          entry.orderEntry?.entryGroupNumbers?.length > 0 &&
          entryGroup.entryGroupNumber === entry.orderEntry?.entryGroupNumbers[0]
      );
      const quantity = entry.shippedQuantity ?? entry.quantity;
      const totalPrice = entry.shippedTotalWithoutDiscount?.formattedValue;
      const discountedPrice =
        entry.shippedTotalWithoutDiscount?.value !== entry.consignedEntryShippedTotal?.value
          ? entry.consignedEntryShippedTotal?.formattedValue
          : undefined;
      if (!linkedEntryGroup || linkedEntryGroup.type === CartGroupTypeEnum.STANDALONE) {
        products.push(
          this.orderEntryToIOrderProduct(
            entry.orderEntry,
            quantity,
            undefined,
            totalPrice,
            discountedPrice,
            undefined,
            undefined,
            isNewPriceManagementActivated
          )
        );
        consignmentEntryGroups.set(CartGroupTypeEnum.STANDALONE, true);
      } else if (linkedEntryGroup.type === CartGroupTypeEnum.BUNDLE) {
        consignmentEntryGroups.set(CartGroupTypeEnum.BUNDLE, true);

        const bundleAlreadyExist = products.find((p) => p.groupNumber === linkedEntryGroup.entryGroupNumber);
        if (bundleAlreadyExist) {
          const index = products.findIndex((p) => p.groupNumber === linkedEntryGroup.entryGroupNumber);
          bundleAlreadyExist.productInfo.bundle = [
            ...bundleAlreadyExist.productInfo.bundle,
            this.orderEntryToIOrderProduct(
              entry.orderEntry,
              quantity,
              undefined,
              totalPrice,
              discountedPrice,
              undefined,
              undefined,
              isNewPriceManagementActivated
            ).productInfo
          ];
          products[index] = bundleAlreadyExist;
        } else {
          const product: IOrderProduct = {
            productInfo: this.cartGroupEntryToIProductCustomer(linkedEntryGroup),
            links: []
          };
          product.productInfo.bundle = [
            this.orderEntryToIOrderProduct(
              entry.orderEntry,
              quantity,
              undefined,
              totalPrice,
              discountedPrice,
              undefined,
              undefined,
              isNewPriceManagementActivated
            ).productInfo
          ];

          products.push({ ...product, groupNumber: linkedEntryGroup.entryGroupNumber });
        }
      }
    });

    if (consignment.returnable) {
      displayBundleWarningMessage = consignmentEntryGroups.get(CartGroupTypeEnum.BUNDLE);
      links.push({
        url: consignmentEntryGroups.get(CartGroupTypeEnum.STANDALONE)
          ? `/return-products/${order.code}/${consignment.code}`
          : '',
        linkName: 'order.details.links.returnProducts',
        external: false,
        disable: !consignmentEntryGroups.get(CartGroupTypeEnum.STANDALONE)
      });
    }

    return {
      orderCode: consignment.code,
      status: this.getConsignmentStatus(consignment),
      shipmentDate: new Date(consignment.statusDate)?.toLocaleDateString(),
      products,
      links,
      displayBundleWarningMessage
    };
  }

  private unconsignedEntriesToIOrderProductDetail(order: CustomOrder, isNewPriceManagementActivated?: boolean): IOrderProductDetail {
    const products: IOrderProductWithGroupNumber[] = [];
    const links: ILink[] = [];
    order.unconsignedEntries?.forEach((entry) => {
      const linkedEntryGroup = order.entryGroups?.find(
        (entryGroup) =>
          entry.entryGroupNumbers?.length > 0 && entryGroup.entryGroupNumber === entry.entryGroupNumbers[0]
      );
      if (!linkedEntryGroup || linkedEntryGroup.type === CartGroupTypeEnum.STANDALONE) {
        products.push(this.orderEntryToIOrderProduct(
          entry,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          isNewPriceManagementActivated
        ));
      } else if (linkedEntryGroup.type === CartGroupTypeEnum.BUNDLE) {
        const bundleAlreadyExist = products.find((p) => p.groupNumber === linkedEntryGroup.entryGroupNumber);
        if (bundleAlreadyExist) {
          const index = products.findIndex((p) => p.groupNumber === linkedEntryGroup.entryGroupNumber);
          bundleAlreadyExist.productInfo.bundle = [
            ...bundleAlreadyExist.productInfo.bundle,
            this.orderEntryToIOrderProduct(
              entry,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              isNewPriceManagementActivated
            ).productInfo
          ];
          products[index] = bundleAlreadyExist;
        } else {
          const product: IOrderProduct = {
            productInfo: this.cartGroupEntryToIProductCustomer(linkedEntryGroup),
            links: []
          };
          product.productInfo.bundle = [this.orderEntryToIOrderProduct(
            entry,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            isNewPriceManagementActivated
          ).productInfo];

          products.push({ ...product, groupNumber: linkedEntryGroup.entryGroupNumber });
        }
      }
    });
    if (order.status === OrderStatusEnum.COMPLETED) {
      if (order.bill?.url) {
        const urlBill = this.documentMapper.normalizeUrl(order.bill.url);
        links.push({ url: urlBill, linkName: 'order.details.links.downloadInvoice', external: true });
      }

      if (order.voucherFileForService?.url) {
        const urlVoucher = this.documentMapper.normalizeUrl(order.voucherFileForService.url);
        links.push({ url: urlVoucher, linkName: 'order.details.links.downloadVoucherFileForService', external: true });
      }

      if (order?.isForfait && order?.serviceShippingIncluded) {
        const linksToPush = [
          { url: order?.customerTransportLabel?.url, linkName: 'order.details.links.customerTransportLabel', external: true },
          { url: order?.urlTrackingForCustomerTransport, linkName: 'order.details.links.urlTrackingCustomerTransport', external: true },
          { url: order?.aspTransportLabel?.url, linkName: 'order.details.links.aspTransportLabel', external: true },
          { url: order?.urlTrackingForAspTransport, linkName: 'order.details.links.urlTrackingAspTransport', external: true }
        ]
          .filter(({ url }) => !!url)
          .map(({ url, linkName, external }) => ({
            url: this.documentMapper.normalizeUrl(url),
            linkName,
            external
          }));
        links.push(...linksToPush);
      }
    }
    return {
      status: this.getConsignmentStatus({ status: undefined }),
      products,
      links
    };
  }

  private productToIOrderProduct(
    product: Product,
    cancelledQuantity: number,
    isTaxesIncluded?: boolean
  ): IOrderProduct {
    const links: ILink[] = [];
    if (!!product && product.instructionsForUse?.instructionsForUse?.length > 0) {
      links.push({
        url: `/instructions-for-use/csp/${product.code}`,
        linkName: 'order.details.links.ifus',
        external: false
      });
    }
    if (!!product && product.faqs?.faqEntries?.length > 0) {
      links.push({ url: `/faq/csp/${product.code}`, linkName: 'order.details.links.faqs', external: false });
    }

    return {
      productInfo: {
        code: product?.code,
        media: getMediaByFormat(product?.primaryImages, 'main') ?? product?.images?.[ImageType.PRIMARY]?.['main'],
        title: product?.name,
        price: product?.price?.formattedValue,
        discountedPrice:
          product?.discountedPrice?.value !== product?.price?.value ? product?.price.formattedValue : undefined,
        totalPrice: undefined,
        quantity: cancelledQuantity,
        url: LinkMapper.decodeUrl(product?.url),
        isTaxesIncluded: isTaxesIncluded
      },
      links
    };
  }

  private getOrderStatus(order: OrderHistory | Order): IOrderStatus {
    switch (order.status) {
      case OrderStatusEnum.IN_PREPARATION:
      case OrderStatusEnum.VALIDATED:
      case OrderStatusEnum.PARTIALLY_DISPATCH:
      case OrderStatusEnum.PENDING:
        return {
          name: 'order.details.statuses.pending',
          value: StatusEnum.warning
        };
      case OrderStatusEnum.CANCELLED:
      case OrderStatusEnum.CANCELED:
        return {
          name: 'order.details.statuses.canceled',
          value: StatusEnum.error
        };
      case OrderStatusEnum.PARTIALLY_SHIPPED_CANCELLED:
      case OrderStatusEnum.SHIPPED:
        return {
          name: 'order.details.statuses.shipped',
          value: StatusEnum.success
        };
      case OrderStatusEnum.COMPLETED:
        return {
          name: 'order.details.statuses.completed',
          value: StatusEnum.success
        };
      case OrderStatusEnum.DELETED:
        return {
          name: 'order.details.statuses.canceled',
          value: StatusEnum.error
        };
      default:
        return {
          name: 'order.details.statuses.canceled',
          value: StatusEnum.error
        };
    }
  }

  private getConsignmentStatus(consignment: CustomConsignment): IOrderStatus {
    switch (consignment.status) {
      case OrderStatusEnum.SHIPPED:
        return {
          name: 'order.details.statuses.shipped',
          value: StatusEnum.success
        };
      case OrderStatusEnum.CANCELLED:
        return {
          name: 'order.details.statuses.canceled',
          value: StatusEnum.error
        };
      default:
        return {
          name: 'order.details.statuses.pending',
          value: StatusEnum.warning
        };
    }
  }
}
